/**
 * Enemy roster (DESIGN 4.3): the kind union, the per-kind stat table and
 * the handful of numbers the 4.3 table quotes that the `EnemyAttack` union
 * cannot carry (bomber blast extras and death bomb, the jotunn's secondary
 * melee, the orc's speed once its armor is gone).
 *
 * Every number here is copied from the 4.3 table. Where a row leaves a
 * field out (melee reach for most rows, melee knockback for spiders and
 * skeletons) the choice is documented next to the value.
 */

import type { Element } from './elements.js';
import { DUMMY } from './constants.js';

export type EnemyKind = 'spider'|'rogue'|'ranger'|'bomber'|'shaman'|'troll'|'treespirit'|'skeleton'|'orc'|'jotunn'|'dummy'|'mire';

export const ENEMY_KINDS: readonly EnemyKind[] = ['spider', 'rogue', 'ranger', 'bomber', 'shaman', 'troll', 'treespirit', 'skeleton', 'orc', 'jotunn', 'dummy', 'mire'];

export type EnemyAttack =
  | { kind:'melee'; dmg:number; range:number; cooldown:number; knockback:number; knockdown?:true }
  | { kind:'arrow'; dmg:number; range:number; keepDistance:number; cooldown:number; speed:number }
  | { kind:'bomb'; dmg:number; fuse:number; radius:number; range:number; cooldown:number }
  | { kind:'waterbeam'; range:number; channel:number; cooldown:number; knockback:number }
  | { kind:'slam'; dmg:number; radius:number; cooldown:number; knockback:number }
  | { kind:'fissure'; dmg:number; length:number; cooldown:number }
  /** Not in the DESIGN 4.3 union, but the dummy row's attack is "none" and the table is unrepresentable without it. */
  | { kind:'none' };

export interface EnemyDef {
  kind:EnemyKind;
  hp:number;
  armor:number;
  speed:number;
  radius:number;
  contactDamage:number;
  attack:EnemyAttack;
  traits:{ undead?:true; noWet?:true; regen?:number /* hp/s unless burning */; immune?:Element[]; fireMul?:number; boss?:true; noFreeze?:true };
}

/**
 * Melee reach, centre to centre. DESIGN 4.3 quotes only the troll's (1.6);
 * the others are attacker radius + PLAYER_RADIUS (0.45) + about 0.2 u of
 * arm, so a melee enemy has to be visibly in contact before it can hit.
 * Legibility over reach: a hit from a body that is not touching you reads
 * as a bug, not as a threat.
 */
const REACH = { small: 1.0, orc: 1.2, troll: 1.6, jotunn: 1.8 } as const;

/**
 * No row in 4.3 has a contact-damage column: bumping into an enemy never
 * hurts, every point of damage comes from a telegraphed attack with a
 * cooldown. The field stays on the def so a future kind can use it.
 */
const NO_CONTACT = 0;

export const ENEMY_DEFS: Record<EnemyKind, EnemyDef> = {
  spider: {
    kind: 'spider', hp: 330, armor: 0, speed: 5.5, radius: 0.35, contactDamage: NO_CONTACT,
    // Knockback not quoted for spiders: 0, so a swarm nibbles rather than juggles.
    attack: { kind: 'melee', dmg: 40, range: REACH.small, cooldown: 1.0, knockback: 0 },
    traits: {},
  },
  rogue: {
    kind: 'rogue', hp: 700, armor: 0, speed: 4.2, radius: 0.4, contactDamage: NO_CONTACT,
    attack: { kind: 'melee', dmg: 90, range: REACH.small, cooldown: 0.8, knockback: 2 },
    traits: {},
  },
  ranger: {
    kind: 'ranger', hp: 700, armor: 0, speed: 3.5, radius: 0.4, contactDamage: NO_CONTACT,
    attack: { kind: 'arrow', dmg: 110, range: 10, keepDistance: 7, cooldown: 2, speed: 16 },
    traits: {},
  },
  bomber: {
    kind: 'bomber', hp: 800, armor: 0, speed: 3.8, radius: 0.4, contactDamage: NO_CONTACT,
    // The blast's burn stacks and knockback, plus the death bomb, are in BOMBER below.
    attack: { kind: 'bomb', dmg: 250, fuse: 1.5, radius: 2, range: 8, cooldown: 3 },
    traits: {},
  },
  shaman: {
    kind: 'shaman', hp: 2000, armor: 0, speed: 3.0, radius: 0.45, contactDamage: NO_CONTACT,
    // 0 damage: the beam only wets and pushes, so lightning is the player's own problem.
    attack: { kind: 'waterbeam', range: 10, channel: 3, cooldown: 6, knockback: 3 },
    traits: {},
  },
  troll: {
    kind: 'troll', hp: 6000, armor: 0, speed: 3.2, radius: 0.9, contactDamage: NO_CONTACT,
    attack: { kind: 'melee', dmg: 450, range: REACH.troll, cooldown: 1.5, knockback: 6, knockdown: true },
    traits: { regen: 60 },
  },
  treespirit: {
    kind: 'treespirit', hp: 2500, armor: 0, speed: 2.2, radius: 0.8, contactDamage: NO_CONTACT,
    attack: { kind: 'slam', dmg: 300, radius: 2, cooldown: 2, knockback: 6 },
    traits: { noWet: true, fireMul: 2 },
  },
  skeleton: {
    kind: 'skeleton', hp: 500, armor: 0, speed: 4.0, radius: 0.4, contactDamage: NO_CONTACT,
    attack: { kind: 'melee', dmg: 80, range: REACH.small, cooldown: 0.9, knockback: 0 },
    traits: { undead: true, fireMul: 0.5 },
  },
  orc: {
    kind: 'orc', hp: 2200, armor: 2200, speed: 3.6, radius: 0.5, contactDamage: NO_CONTACT,
    attack: { kind: 'melee', dmg: 200, range: REACH.orc, cooldown: 1.2, knockback: 4 },
    traits: {},
  },
  jotunn: {
    kind: 'jotunn', hp: 12000, armor: 0, speed: 3.0, radius: 1.0, contactDamage: NO_CONTACT,
    // The fissure always knocks down (DESIGN 4.3 "fissure 500 + knockdown"); the
    // variant has no flag because there is no fissure without one.
    attack: { kind: 'fissure', dmg: 500, length: 5, cooldown: 3 },
    traits: { immune: ['lightning'], boss: true },
  },
  dummy: {
    kind: 'dummy', hp: DUMMY.hp, armor: 0, speed: 0, radius: 0.5, contactDamage: NO_CONTACT,
    attack: { kind: 'none' },
    traits: {},
  },
  // Story act 1's boss (docs/spellweave-modes.md §1), not in DESIGN 4.3: the Tide's matriarch, a giant shaman. Her
  // attack is the shaman's water beam, bigger; her phases, slam, flood and sinking live in server enemies/mire.ts.
  // Radius 1.0 like the jotunn, the largest body the broadphase expects; the art stands 3 u tall. Cold chills her
  // but never freezes her solid (her own flood soaks her, and frozen x3 on top of stranded x2 ended her in seconds).
  mire: {
    kind: 'mire', hp: 15000, armor: 0, speed: 2.4, radius: 1.0, contactDamage: NO_CONTACT,
    attack: { kind: 'waterbeam', range: 14, channel: 4, cooldown: 5, knockback: 6 },
    traits: { boss: true, noFreeze: true },
  },
};

/** DESIGN 4.3 bomber row: the numbers the `bomb` attack variant cannot carry. */
export const BOMBER = {
  /** Burn stacks applied to everything in the thrown bomb's blast. */
  burnStacks: 2,
  /** Knockback of the thrown bomb's blast. */
  knockback: 8,
  /** The live bomb a bomber drops when it dies: fire damage and blast radius. */
  deathBomb: { dmg: 400, radius: 2.5 },
} as const;

/** Orc speed once its armor pool is gone (DESIGN 4.3 "speed 4.4 once armor gone"). */
export const ORC_UNARMORED_SPEED = 4.4;

/**
 * The jotunn's second attack (DESIGN 4.3 "melee 350") for players inside
 * its reach; `ENEMY_DEFS.jotunn.attack` is the fissure. Cooldown and
 * knockback are not quoted: 1.5 s keeps the fissure as the main threat and
 * the shove is troll-sized so a boss swipe visibly moves you.
 */
export const JOTUNN_MELEE: EnemyAttack = { kind: 'melee', dmg: 350, range: REACH.jotunn, cooldown: 1.5, knockback: 6 };

/** The def for a kind; the table is total so this never returns undefined. */
export function enemyDef(kind: EnemyKind): EnemyDef {
  return ENEMY_DEFS[kind];
}

/** Movement speed of a kind given its current armor pool: orcs speed up once shattered. */
export function enemySpeed(def: EnemyDef, armor: number): number {
  if (def.kind === 'orc' && def.armor > 0 && armor <= 0) return ORC_UNARMORED_SPEED;
  return def.speed;
}

export function isBossKind(kind: EnemyKind): boolean {
  return ENEMY_DEFS[kind].traits.boss === true;
}
